import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Repeat2, ShieldCheck } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";

import {
  signupInputSchema,
  type SignupAcceptedResponse,
  type SignupInput,
} from "@learn-flow/contracts";

import { Logo } from "../../components/logo.js";
import { ApiError, apiRequest } from "../../lib/api.js";
import { FullPageStatus } from "./login-page.js";
import { sessionQueryOptions } from "./session.js";
import { GoogleSignIn } from "./google-sign-in.js";

export function SignupPage(): React.JSX.Element {
  const sessionQuery = useQuery(sessionQueryOptions);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupInputSchema),
    defaultValues: { email: "", password: "", termsAccepted: true },
  });
  const signup = useMutation({
    mutationFn: (input: SignupInput) =>
      apiRequest<SignupAcceptedResponse>("/api/v1/auth/signup", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onError: (error: Error) => {
      setError("root", {
        message:
          error instanceof ApiError && error.status === 429
            ? "Too many signup attempts. Please try again later."
            : "We couldn’t start your signup. Please try again.",
      });
    },
  });

  useEffect(() => {
    document.title = "Sign up · MemoKite";
  }, []);

  if (sessionQuery.isLoading) {
    return <FullPageStatus message="Opening MemoKite…" />;
  }
  if (sessionQuery.data) return <Navigate to="/today" replace />;

  return (
    <main className="auth-shell">
      <section className="auth-panel" aria-labelledby="signup-heading">
        <Logo />
        <div className="auth-form-wrap">
          <span className="section-kicker">Create your account</span>
          <h1 id="signup-heading">Begin your learning flow</h1>
          <p>Use your email and verify it before entering your private learning space.</p>

          {signup.isSuccess ? (
            <div className="auth-success" role="status">
              <h2>Check your email</h2>
              <p>{signup.data.message}</p>
              <p>The verification link expires in 24 hours.</p>
              <Link to="/login">Go to login</Link>
            </div>
          ) : (
            <form
              className="auth-email-form"
              onSubmit={handleSubmit((input) => {
                if (!signup.isPending) signup.mutate(input);
              })}
              noValidate
            >
              <label htmlFor="signup-email">
                Email
                <input
                  id="signup-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-invalid={errors.email ? "true" : undefined}
                  disabled={signup.isPending}
                  {...register("email")}
                />
              </label>
              {errors.email?.message && <p className="field-error">{errors.email.message}</p>}

              <label htmlFor="signup-password">
                Password
                <input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  aria-describedby="signup-password-help"
                  aria-invalid={errors.password ? "true" : undefined}
                  disabled={signup.isPending}
                  {...register("password")}
                />
              </label>
              <p id="signup-password-help" className="field-help">
                Use 8–256 characters with at least one letter and one number.
              </p>
              {errors.password?.message && <p className="field-error">{errors.password.message}</p>}

              <div className="auth-message" aria-live="assertive" aria-atomic="true">
                {errors.root?.message && (
                  <p className="login-error" role="alert">
                    {errors.root.message}
                  </p>
                )}
              </div>
              <button
                className="primary-button"
                type="submit"
                aria-describedby="signup-disclosure"
                disabled={signup.isPending}
              >
                {signup.isPending ? (
                  <>
                    <span className="spinner" aria-hidden="true" />
                    Creating account…
                  </>
                ) : (
                  "Sign up"
                )}
              </button>
            </form>
          )}
          <p id="signup-disclosure" className="auth-signup-disclosure">
            By signing up with email or continuing with Google, you confirm that you are at least 16
            and agree to the <Link to="/terms">Terms of Service</Link> and acknowledge the{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
          <GoogleSignIn disclosureId="signup-disclosure" />
          <p className="auth-switch">
            Already have an account? <Link to="/login">Log in</Link>
          </p>
        </div>
        <div className="auth-trust">
          <ShieldCheck size={15} aria-hidden="true" />
          Email verification is required before access.
        </div>
      </section>
      <aside className="auth-visual" aria-hidden="true">
        <div className="auth-orbit auth-orbit-one" />
        <div className="auth-orbit auth-orbit-two" />
        <div className="auth-visual-copy">
          <span>Collect · Plan · Revisit</span>
          <blockquote>Make time for what matters.</blockquote>
        </div>
        <div className="auth-book">
          <span />
          <span />
        </div>
        <div className="auth-mini-card auth-mini-card-one">
          <i className="lavender-dot" />
          <span>Read with intention</span>
          <Check size={14} />
        </div>
        <div className="auth-mini-card auth-mini-card-two">
          <i className="sage-dot" />
          <span>Return with purpose</span>
          <Repeat2 size={14} />
        </div>
      </aside>
    </main>
  );
}
